/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';
import config from './config';

const { AZURE_CLIENT_ID, AZURE_TENANT_ID } = config;

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: AZURE_CLIENT_ID!,
    authority: `https://login.microsoftonline.com/${AZURE_TENANT_ID}`,
    redirectUri: `${window.location.origin}${process.env.PUBLIC_URL}`,
  },
  cache: {
    cacheLocation: 'sessionStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
        case LogLevel.Error:
          console.error(message);
          return;
        case LogLevel.Info:
          console.info(message);
          return;
        case LogLevel.Verbose:
          console.debug(message);
          return;
        case LogLevel.Warning:
          console.warn(message);
          return;
        default:
          console.log(message);
          return;
        }
      }
    }
  }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: ['User.Read']
};

export const graphTokenRequest = {
  scopes: ['User.Read']
};

export const backendApiTokenRequest = {
  scopes: [`api://${AZURE_CLIENT_ID || 'dummy-backend-id'}/user_impersonation`]
};

/**
 * Token auto-refresh configuration
 */
export const TOKEN_REFRESH_CONFIG = {
  // Buffer time before token expiration to trigger refresh (seconds)
  // NOTE: Set this to a value GREATER than your token's expiresIn for quick testing
  // Example: If token expires in 4542 seconds, set to 4532 to test in 10 seconds
  // Production: Use 300 (5 minutes)
  REFRESH_BUFFER_TIME: 300,  // FOR TESTING: Will refresh in ~10 seconds
  
  // Maximum retry attempts on refresh failure
  MAX_RETRY_ATTEMPTS: 3,
  
  // Base delay for retry with exponential backoff (milliseconds)
  RETRY_BASE_DELAY: 1000,
  
  // Enable debug logging for development
  ENABLE_DEBUG_LOG: true,
} as const;

/**
 * Token Auto-Refresh Manager
 * Responsibilities:
 * 1. Calculate refresh timing based on expiresIn
 * 2. Manage refresh timer
 * 3. Execute refresh logic
 * 4. Handle retry on failure with exponential backoff
 */
export class TokenRefreshManager {
  private timerId: NodeJS.Timeout | null = null;
  private retryCount: number = 0;

  /**
   * Start automatic token refresh
   * @param expiresIn Token validity period in seconds
   * @param refreshCallback Callback function to refresh token, should return token info
   */
  startAutoRefresh(
    expiresIn: number,
    refreshCallback: () => Promise<{ token: string; expiresOn: Date | null }>
  ): void {
    // Calculate refresh delay (refresh 5 minutes before expiration)
    const refreshDelay = Math.max(
      (expiresIn - TOKEN_REFRESH_CONFIG.REFRESH_BUFFER_TIME) * 1000,
      0
    );

    if (TOKEN_REFRESH_CONFIG.ENABLE_DEBUG_LOG) {
      console.log('[TokenRefresh] Setting up auto-refresh', {
        expiresIn,
        refreshDelaySeconds: refreshDelay / 1000,
        refreshTime: new Date(Date.now() + refreshDelay).toLocaleString()
      });
    }

    // Clear existing timer
    this.stopAutoRefresh();

    // Set new timer
    this.timerId = setTimeout(async () => {
      await this.executeRefresh(refreshCallback);
    }, refreshDelay);
  }

  /**
   * Stop automatic refresh and clear timer
   */
  stopAutoRefresh(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.retryCount = 0;
  }

  /**
   * Execute refresh logic with retry mechanism
   * @param refreshCallback Callback function to refresh token
   * @param tokenInfo Optional token info for debugging
   */
  private async executeRefresh(
    refreshCallback: () => Promise<{ token: string; expiresOn: Date | null }>
  ): Promise<void> {
    try {
      const result = await refreshCallback();

      if (TOKEN_REFRESH_CONFIG.ENABLE_DEBUG_LOG) {
        console.log('[TokenRefresh] Token refreshed successfully', {
          newToken: result.token.substring(0, 20) + '...',
          expiresOn: result.expiresOn?.toLocaleString() || 'N/A',
          expiresInSeconds: result.expiresOn
            ? Math.floor((result.expiresOn.getTime() - Date.now()) / 1000)
            : 'N/A'
        });
      }

      // Reset retry count on success
      this.retryCount = 0;

    } catch (error) {
      console.error('[TokenRefresh] Token refresh failed', error);

      // Retry logic with exponential backoff
      if (this.retryCount < TOKEN_REFRESH_CONFIG.MAX_RETRY_ATTEMPTS) {
        this.retryCount++;
        const retryDelay = TOKEN_REFRESH_CONFIG.RETRY_BASE_DELAY * Math.pow(2, this.retryCount - 1);

        console.warn(
          `[TokenRefresh] Retrying in ${retryDelay}ms (${this.retryCount}/${TOKEN_REFRESH_CONFIG.MAX_RETRY_ATTEMPTS})`
        );

        setTimeout(() => {
          this.executeRefresh(refreshCallback);
        }, retryDelay);
      } else {
        console.error('[TokenRefresh] Max retry attempts reached, please re-login');
        this.retryCount = 0;
      }
    }
  }
}

/**
 * Factory function to create TokenRefreshManager instance
 */
export const createTokenRefreshManager = () => new TokenRefreshManager();